import mapboxgl from 'mapbox-gl';
import {
  kebabCaseKeys,
  selectiveMerge
} from "utilities/object_helpers";
import { pointFeatureToPopupContent } from "utilities/mapbox_style_adapters";
import tinycolor from "tinycolor2";

export default class BaseMarker {
  constructor(pointFeature, options) {
    this._validateAllStaticMethodsImplemented();
    this.pointFeature = pointFeature;
    this.map = options.map;

    if(!this.pointFeature.properties) {
      this.pointFeature.properties = { icon: 'hospital'};
    }
    this.iconUrl = options.assets.icons[this.pointFeature.properties.icon];
    this.markerUrl = options.assets.markers[this.constructor.markerType()].path;

    this._setStyleOptions(options.style || {});

    const element = this._createDivMarker();
    this.marker = new mapboxgl.Marker(element, { anchor: this.constructor.anchor() });
    this.marker.setLngLat(this.pointFeature.geometry.coordinates);

    if (pointFeature.properties.form) {
      const content = pointFeatureToPopupContent(pointFeature, {
        excludeCoordinateLink: !options.pointFeatureSet.popup.show_coordinates
      });
      const popup = new mapboxgl
        .Popup({
          offset: this.constructor.popupOffsets(),
          closeButton: false,
          focusAfterOpen: false
        })
        .setHTML(content);
      this.marker.setPopup(popup);
    }
  }

  // Whether the icon should resolve to static properties. Returning true in an
  // inheriting class will cause the marker to adopt the "fill" style
  // property.
  //
  static allowIconFillInjection() {
    return false;
  }

  static dimensions() {
    return {
      "width": 30,
      "height": 42
    };
  }

  static popupOffsets() {
    return {
      'top': [0, 0],
      'top-left': [0, 0],
      'top-right': [0, 0],
      'bottom': [0, -this.dimensions().height],
      'bottom-left': [0, -this.dimensions().height],
      'bottom-right': [0, -this.dimensions().height],
      'left': [this.dimensions().width / 2, (this.dimensions().height / 2) * -1],
      'right': [(this.dimensions().width / 2) * -1, (this.dimensions().height / 2) * -1]
    };
  }

  hide() {
    this.marker.remove();
  }

  show() {
    this.marker.addTo(this.map);
  }

  setPosition(point) {
    this.marker.setLngLat(point);
  }

  // @param [Object] style
  //   @option [Boolean] style.icon
  //   @option [String] style.fill
  //   @option [String] style.stroke
  //   @option [String] style.stroke-width
  //   @option [Float] style.stroke-opacity
  //
  _setStyleOptions(style) {
    this.iconStyle = this.constructor.iconStyle(this.pointFeature.properties.icon);
    const markerColor = this.pointFeature.properties.color || style.fill;

    if (this.constructor.allowIconFillInjection()) {
      this.iconSvgAttributes = {
        ...this.constructor.iconSvgAttributes(),
        ...{ fill: markerColor }
      };
    } else {
      const iconColor = tinycolor(markerColor).isLight() ? "#000000" : "#ffffff";
      this.iconSvgAttributes = {
        ...this.constructor.iconSvgAttributes(),
        ...{ fill: iconColor }
      };
    }

    this.markerStyle = this.constructor.markerStyle();
    this.markerSvgAttributes = selectiveMerge(
      this.constructor.markerSvgAttributes(),
      selectiveMerge(kebabCaseKeys(style), { fill: this.pointFeature.properties.color })
    );

    this._showIcon = style.icon;
  }

  _createDivMarker() {
    const div = document.createElement('div');

    const marker = this._createSvgObject(
      this.markerUrl,
      this.markerStyle,
      this.markerSvgAttributes
    );
    div.appendChild(marker);

    if (this._showIcon) {
      const icon = this._createSvgObject(
        this.iconUrl,
        this.iconStyle,
        this.iconSvgAttributes
      );
      div.appendChild(icon);
    }

    return div;
  }

  // Creates an object Element with the SVG specified by the
  // passed URL. Options include specifying the size of the
  // marker and attributes for the marker.
  //
  // @param {String} url
  // @param {Object} style
  // @param {Object} svgAttributes
  //
  // @return {Element<object>}
  //
  _createSvgObject(url, style, svgAttributes) {
    let svgObject = document.createElement("object");

    svgObject.setAttribute("data", url);
    this._setStylesOn(svgObject, style);

    svgObject.addEventListener("load", event => {
      let elements = event.target.getSVGDocument().querySelectorAll("circle, path, polygon");
      elements.forEach(element => {
        this._setAttributesOn(element, svgAttributes);
      });
    });

    return svgObject;
  }

  // Helper method to set numerous attributes to an
  // element.
  //
  // @param {Element} element
  // @param {Object} attrs
  //
  _setAttributesOn(element, attrs = {}) {
    for (var key in attrs) {
      element.setAttribute(key, attrs[key]);
    }
  }

  _setStylesOn(element, attrs = {}) {
    for (var key in attrs) {
      element.style[key] = attrs[key];

      if (element.style[key] != attrs[key]) throw `Style ${attrs[key]} not in proper format`;
    }
  }

  _validateAllStaticMethodsImplemented() {
    const requiredStaticMethods = [
      "anchor",
      "iconStyle",
      "iconSvgAttributes",
      "markerStyle",
      "markerSvgAttributes",
      "markerType"
    ];
    requiredStaticMethods.forEach((requiredStaticMethod) => {
      if (this.constructor.hasOwnProperty(requiredStaticMethod)) return;

      throw `
        Must override all requisite static methods to use BaseMarker class;
        static method "${requiredStaticMethod}" missing.
      `;
    });
  }
}
